import React from "react"

import { format, formatDistanceToNow } from "date-fns"
import { ChevronDown, MessageSquareText } from "lucide-react"

import { CopyButton } from "@/components/CopyButton"
import Icon from "@/components/Icon"
import { AuditActorDetails } from "@/components/audit/AuditActorDetails"
import {
  formatFieldLabel,
  getEntityBadgeClassName,
  getEntityLabel,
} from "@/components/sheriff/shift/shiftAuditConfig"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { AuditLogChange, ShiftAuditLog } from "@/hooks/useShiftAuditLogs"
import { ukFormat } from "@/lib/ukFormat"
import { cn } from "@/lib/utils"
import { auditActorLabel } from "@/utils/auditActor"
import { auditValueText } from "@/utils/auditValue"

const EMPTY_VALUE_LABEL = "Empty"

const formatAuditTimestamp = (value: string) => {
  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return {
      absolute: value,
      relative: "",
    }
  }

  return {
    absolute: format(parsed, "dd MMM yyyy, HH:mm"),
    relative: formatDistanceToNow(parsed, { addSuffix: true }),
  }
}

const isDateLikeString = (value: string) => {
  if (!value.trim()) return false
  if (!/^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]*)?$/.test(value)) return false

  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime())
}

const renderAuditValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return <span className="italic text-palette-jungleGreen500">{EMPTY_VALUE_LABEL}</span>
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No"
  }

  if (typeof value === "number") {
    return value.toString()
  }

  if (typeof value === "string") {
    if (isDateLikeString(value)) {
      return ukFormat(
        value,
        value.includes("T") || value.includes(":") ? "dd MMM yyyy, HH:mm" : "dd MMM yyyy",
      )
    }

    return value
  }

  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-5 text-current">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

const AuditValueCard: React.FC<{
  field: string
  tone: "before" | "after"
  value: unknown
}> = ({ field, tone, value }) => {
  const toneClasses =
    tone === "before"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-green-200 bg-green-50 text-green-800"
  const valueClasses = tone === "before" ? "line-through" : ""
  // copies the stored value rather than the formatted one, so a date comes out as ISO
  const text = auditValueText(value)

  return (
    <div
      className={cn("flex min-w-0 items-start gap-1 rounded-md border px-2 py-1.5", toneClasses)}
    >
      <div className={cn("min-w-0 flex-1 break-words text-xs leading-5", valueClasses)}>
        {renderAuditValue(value)}
      </div>
      {text ? (
        <CopyButton
          value={text}
          label={`${formatFieldLabel(field)} ${tone}`}
          className="-my-0.5 -mr-1 h-5 w-5 text-current hover:bg-white/60 hover:text-current"
        />
      ) : null}
    </div>
  )
}

const AuditChangeRow: React.FC<{
  field: string
  change: AuditLogChange
}> = ({ field, change }) => (
  <div className="rounded-lg border border-palette-jungleGreen200 bg-white p-2.5 shadow-sm">
    <div className="mb-1.5 text-xs font-medium text-palette-jungleGreen700">
      {formatFieldLabel(field)}
    </div>
    <div className="grid gap-1.5 sm:grid-cols-2">
      <AuditValueCard field={field} tone="before" value={change.before} />
      <AuditValueCard field={field} tone="after" value={change.after} />
    </div>
  </div>
)

const AuditInsertFieldRow: React.FC<{
  field: string
  value: unknown
}> = ({ field, value }) => (
  <div className="rounded-lg border border-palette-jungleGreen200 bg-white p-2.5 shadow-sm">
    <div className="mb-1.5 text-xs font-medium text-palette-jungleGreen700">
      {formatFieldLabel(field)}
    </div>
    <AuditValueCard field={field} tone="after" value={value} />
  </div>
)

const AuditCommentSection: React.FC<{
  comment: string
}> = ({ comment }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="mt-2 overflow-hidden rounded-md border border-palette-jungleGreen200 bg-palette-jungleGreen000/80">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-palette-jungleGreen100"
            aria-label={open ? "Hide audit comment" : "Show audit comment"}
          >
            <div className="flex min-w-0 items-center gap-1.5 text-palette-jungleGreen600">
              <MessageSquareText className="h-3.5 w-3.5 shrink-0" />
              <div className="text-[11px] font-medium uppercase tracking-wide">Audit Comment</div>
            </div>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-palette-jungleGreen500 transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-palette-jungleGreen200 px-2.5 py-2 text-sm leading-5 text-palette-jungleGreen700 whitespace-pre-wrap break-words">
          {comment}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

const AuditActorSection: React.FC<{
  entry: ShiftAuditLog
}> = ({ entry }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="mt-2 overflow-hidden rounded-md border border-palette-jungleGreen200 bg-palette-jungleGreen000/80">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-palette-jungleGreen100"
            aria-label={open ? "Hide actor details" : "Show actor details"}
          >
            <div className="flex min-w-0 items-center gap-1.5 text-palette-jungleGreen600">
              <Icon name="user" className="h-3.5 w-3.5 shrink-0 text-current" />
              <div className="text-[11px] font-medium uppercase tracking-wide">Actor Details</div>
            </div>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-palette-jungleGreen500 transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-palette-jungleGreen200 p-2">
          <AuditActorDetails entry={entry} className="bg-white" />
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export const ShiftAuditEntryCard: React.FC<{
  entry: ShiftAuditLog
  changes: Array<[string, AuditLogChange]>
}> = ({ entry, changes }) => {
  const timestamp = formatAuditTimestamp(entry.created_at)
  const isInsert = entry.action === "insert"
  const [showInsertDetails, setShowInsertDetails] = React.useState(false)
  const normalizedComment = entry.comment?.trim() ?? ""

  return (
    <article className="rounded-xl border border-palette-jungleGreen200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-palette-jungleGreen900">
            {auditActorLabel(entry)}
          </div>
          <div className="mt-1">
            <Badge
              variant="outline"
              className={cn(
                "h-5 min-w-5 rounded-md px-1.5 text-[9px] uppercase tracking-wide",
                getEntityBadgeClassName(entry.table_name),
              )}
            >
              {getEntityLabel(entry.table_name)}
            </Badge>
          </div>
        </div>
        <div className="shrink-0 text-right text-[11px] text-palette-jungleGreen500">
          <div>{timestamp.absolute}</div>
          {timestamp.relative ? <div>{timestamp.relative}</div> : null}
        </div>
      </div>

      <AuditActorSection entry={entry} />
      {normalizedComment ? <AuditCommentSection comment={normalizedComment} /> : null}

      {isInsert ? (
        <Collapsible open={showInsertDetails} onOpenChange={setShowInsertDetails}>
          {changes.length > 0 ? (
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 px-2.5 py-2 text-left transition-colors hover:bg-green-100"
                aria-label={showInsertDetails ? "Hide created fields" : "Show created fields"}
              >
                <span className="text-sm text-green-800">
                  {getEntityLabel(entry.table_name)} created.
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-green-700 transition-transform",
                    showInsertDetails && "rotate-180",
                  )}
                />
              </button>
            </CollapsibleTrigger>
          ) : (
            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-2.5 py-2 text-sm text-green-800">
              {getEntityLabel(entry.table_name)} created.
            </div>
          )}

          {changes.length > 0 ? (
            <CollapsibleContent className="mt-2 space-y-2">
              {changes.map(([field, change]) => (
                <AuditInsertFieldRow
                  key={`${entry.id}-${field}`}
                  field={field}
                  value={change.after}
                />
              ))}
            </CollapsibleContent>
          ) : null}
        </Collapsible>
      ) : (
        <div className="mt-3 space-y-2">
          {changes.map(([field, change]) => (
            <AuditChangeRow key={`${entry.id}-${field}`} field={field} change={change} />
          ))}
        </div>
      )}
    </article>
  )
}
